import { setMockPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import SimpleRankingPage from 'pages/SimpleRanking/SimpleRankingPage';
import { getFranchiseBySlugAndYear } from 'api/Ranking/getFranchiseBySlugAndYear';
import type { GetStaticProps } from 'next';
import setWellPageAdConfig from 'services/Ad/config/setWellPageAdConfig';
import type { FranchisePage } from 'interfaces/content/Ranking';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import getFooter from 'api/Footer/getFooter';
import type { HeaderNav } from 'interfaces/navigation/Header';
import { wellLogo } from 'icons/WellLogo';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { Microsites } from 'constants/constants';
import type { PreviewData } from 'interfaces/PreviewData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface WellListProps {
  franchise: FranchisePage;
  subDomain: string;
}

const WellList = ({ franchise, subDomain }: WellListProps) => (
  <SimpleRankingPage
    franchiseList={franchise}
    subDomain={subDomain}
    hidePresentedBy
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const platformCD: string = Microsites.Well;
    const { slug, year } = context.params as { slug: string; year: string };
    const { previewData } = context as { previewData: PreviewData };

    const franchise = await getFranchiseBySlugAndYear(slug, platformCD, year, previewData);
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_WELL', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );

    const pageAdConfig = safe(() => setWellPageAdConfig(franchise), fallbackData.pageAdConfig);
    const dataLayer = safe(
      () => setPageGtmConfig({ ...franchise, platformCD: Microsites.Well }),
      fallbackData.dataLayer,
    );
    const headData = safe(() => setHeadData(franchise, false, 'Fortune Well'), fallbackData.headData);
    return {
      props: {
        dataLayer,
        footer,
        franchise,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig: setMockPianoConfig(),
        subDomain: menu?.subDomain,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths = () => {
  const params = {
    slug: 'best-places-families',
    year: '2022',
  };
  const paths = [{ params: { ...params } }, { params: { ...params } }];

  return {
    fallback: 'blocking',
    paths,
  };
};

export default WellList;
